import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CommandeClientsService } from "app/features/commande-client/services/commandes-client.service";
import { SnackService } from "app/shared/services/snack-service.service";
import { TranslateUtils } from "app/shared/utils/translate.utils";
import { Observable, of, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { AuthService } from "../features/auth/services/auth.service";
import { PingService } from "../shared/services/ping.service";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private error503inProgress: boolean = false;

  constructor(
    private authService: AuthService,
    private snackService: SnackService,
    private commandeClientService: CommandeClientsService,
    private pingService: PingService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    let headers: HttpHeaders = req.headers;
    // ⚠️ Flag capturé PAR REQUÊTE (const locale), jamais sur `this` :
    // l'interceptor est un singleton et le `catchError` s'exécute de façon
    // asynchrone — une propriété d'instance était écrasée par chaque requête
    // concurrente (race), et surtout elle n'était JAMAIS LUE dans le
    // catchError : « skip-error-interceptor » n'a donc jamais silencié les
    // erreurs standard (bug historique, corrigé des deux côtés le 24/07).
    const skipErrorInterceptor = headers.has("skip-error-interceptor");
    headers = headers.delete("skip-error-interceptor");

    this.commandeClientService.resetState();

    return this.interceptHttpError(
      next,
      req.clone({ headers }),
      skipErrorInterceptor,
    );
  }

  /**
   * Vérifie si erreur HTTP interceptée (0 || 404) et affiche un toastr d'erreur.
   * @param {HttpHandler} next
   * @param {HttpRequest<any>} req
   * @returns {Observable<any>}
   */
  private interceptHttpError(
    next: HttpHandler,
    req: HttpRequest<any>,
    skipErrorInterceptor = false,
  ): Observable<any> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Requête marquée silencieuse (skip-error-interceptor) : aucun snack
        // ni règle globale — l'appelant affiche l'erreur dans son contexte
        // (ex. warning inline lots-disponibles). L'erreur remonte telle quelle.
        let message = this.resolveErrorMessage(error);

        const chunkFailedMessage = /Loading chunk [\d]+ failed/;

        if (chunkFailedMessage.test(message)) {
          window.location.reload();
        }

        // L'appelant gère lui-même l'affichage de l'erreur (ex. retry TOCTOU
        // 409/412 du rebind/apply-edit, où l'interceptor afficherait un snack
        // parasite par-dessus le warn de la facade). On relaie l'erreur sans
        // snack — SAUF les cas système globaux, qui restent gérés :
        //   - le rechargement de chunk, testé juste au-dessus ;
        //   - le 503, qui déclenche le logout et le debounce.
        //
        // ⚠️ Les deux branches ont corrigé ce bug indépendamment, mais SEULE
        // celle de `testing` excluait le 503. Sans cette exclusion, une requête
        // marquée `skip-error-interceptor` qui échoue en 503 ne déconnecte plus
        // l'utilisateur, et un chunk-load-failed marqué skip ne recharge plus la
        // page. Ne pas retirer la condition (réintégration du 10/08/2026).
        if (skipErrorInterceptor && error.status !== 503) {
          return throwError(() => error);
        }

        // L'appelant gère lui-même l'affichage de l'erreur (ex: retry TOCTOU
        // 409/412 du rebind/apply-edit, où l'interceptor afficherait un snack
        // d'erreur parasite par-dessus le warn de la facade). On relaie l'erreur
        // sans snack — sauf les cas système globaux (chunk reload ci-dessus,
        // 503 logout) qui restent gérés.
        if (skipErrorInterceptor && error.status !== 503) {
          return throwError(() => error);
        }

        if (req.responseType === "blob") {
          try {
            message = JSON.parse(error?.error?.text()).message;
          } catch (e) {
            message = error?.error?.text()?.toString() ?? error?.error?.text();
          }
        }

        switch (error.status) {
          //case 412:
          //  this.snackService.error(message);
          //  return of(undefined);
          case 0:
            //this.pingService._offline$.next(true);
            return throwError(error);
          case 500:
            this.traiteError(error);
            return throwError(error);
          case 503:
            if (this.error503inProgress) {
              return of(undefined);
            }
            this.error503inProgress = true;
            this.snackService.error(message);
            this.authService.logout();
            setTimeout(() => {
              this.error503inProgress = false;
            }, 1000);
            return of(undefined);
          case 401:
            this.traiteError(error);
            //this.authService.logout();
            return throwError(error);
          case 404:
            this.snackService.error(message);
            return throwError(error);
          //return of(undefined);
          default:
            this.snackService.error(message);
            return throwError(error);
        }
      }),
    );
  }

  /**
   * Résout le message d'erreur : tente de traduire l'errorCode du back,
   * sinon fallback sur le message brut.
   */
  private resolveErrorMessage(error: HttpErrorResponse): string {
    const errorCode = error?.error?.errorCode;
    const params = error?.error?.params;
    const fallback = error?.error?.message;

    if (errorCode) {
      const translated = TranslateUtils.translate(
        `errors.${errorCode}`,
        params,
      );
      // Si la traduction retourne la clé elle-même, c'est qu'elle n'existe pas
      if (translated && translated !== `errors.${errorCode}`) {
        return translated;
      }
    }

    return fallback;
  }

  private traiteError(error: HttpErrorResponse) {
    const apiUrl = new URL(error.url || "");
    const path = apiUrl.pathname;

    // Cherche une règle correspondant à l'URL et dont la condition est vraie
    const matchingRule = this.errorRules.find(
      (rule) =>
        path.startsWith(rule.url) &&
        rule.condition(error.error.message) &&
        (!rule.status || rule.status === error.status),
    );
    if (matchingRule) {
      matchingRule.callback(error?.error.message);
    } else {
      this.snackService.error(this.resolveErrorMessage(error));
    }
  }

  extractTableName(message: string): string | null {
    const match = message.match(
      /violates foreign key constraint .* on table "(.*?)"/,
    );
    return match ? match[1] : null;
  }

  errorRules = [
    {
      url: "/api/specification_etape",
      condition: (message: string) =>
        message.includes("violates foreign key constraint"),
      callback: (message: string) => {
        const tableName = this.extractTableName(message);
        if (tableName) {
          this.snackService.info(
            `Cet élément est lié à un élément "${tableName}". Il sera archivé.`,
          );
        } else {
          this.snackService.info(
            "Cet élément est lié à un autre élément. Il ne peut pas être supprimé",
          );
        }
      },
    },
    {
      url: "/doc/api",
      status: 401,
      condition: (message: string) => {
        return message.includes("Token validation failed");
      },
      callback: (_message: string) => {
        // Avant de bouter l'utilisateur en soft-logout, on tente un refresh
        // SSO : sur les fenêtres détachées (window.open du widget supervision
        // ou de la fiche OF), la première requête doc peut tomber sur un SSO
        // qui vient juste d'expirer dans cette nouvelle session — un simple
        // refresh suffit, pas besoin de redemander le mot de passe.
        this.tryRefreshThenLogout();
      },
    },
  ];

  private async tryRefreshThenLogout(): Promise<void> {
    try {
      await this.authService.ensureRefreshSso();
      console.log("[doc 401] SSO refreshed — user kept logged in");
    } catch (e) {
      console.log("[doc 401] SSO refresh failed → soft logout", e);
      this.authService.softLogout();
    }
  }
}
